import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import 'dayjs/locale/ru'
import 'dayjs/locale/en'
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material'

import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageBreadcrumbs } from '@/components/common/PageBreadcrumbs'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { HomeworkItemsView } from '@/components/homeworks/HomeworkItemsView'
import { useAuthStore } from '@/stores/authStore'
import type { Homework } from '@/types'
import { homeworksApi } from '@/api/homeworks'
import { ROUTES } from '@/utils/constants'
import { getErrorMessage } from '@/utils/error'
import { getUserDisplayName } from '@/utils/userName'
import '@/styles/dashboard.css'

/** Имя из самой домашки; пусто — подпись «Ученик #id» ставит вызывающий */
function formatStudentNameFromHomework(hw: Homework): string {
  const s = hw.student
  if (!s) return ''
  return getUserDisplayName({ ...s, id: null })
}

/**
 * Домашние задания одного ученика — учительский экран.
 *
 * Раздел «На проверке» показывает только то, что ждёт проверки. Всё остальное —
 * назначенные работы, домашка, на которую ученик ответил, но не нажал «сдать»,
 * и уже принятые работы — видно здесь, и отсюда же работу возвращают на
 * доработку.
 */
export function UserHomeworksPage() {
  const { t, i18n } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const userId = id ? parseInt(id, 10) : NaN

  const authUser = useAuthStore((s) => s.user)
  const authUserId = authUser?.id ?? null
  const isAdminRole = (authUser?.roles ?? []).some((r) => r.slug === 'admin')

  const formatCreatedAt = (iso?: string | null) => {
    if (!iso) return '—'
    dayjs.locale(i18n.language?.startsWith('en') ? 'en' : 'ru')
    return dayjs(iso).format('DD.MM.YYYY HH:mm')
  }

  const [homeworks, setHomeworks] = useState<Homework[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [menu, setMenu] = useState<{ anchor: HTMLElement; homework: Homework } | null>(null)
  const [revision, setRevision] = useState<{ homework: Homework; comment: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(10)

  const studentName = useMemo(
    () => homeworks.map(formatStudentNameFromHomework).find((name) => name !== '') ?? '',
    [homeworks],
  )

  const fetchHomeworks = useCallback(async () => {
    if (!Number.isFinite(userId) || !authUserId) return
    setLoading(true)
    setError(null)
    try {
      setHomeworks(await homeworksApi.getByUser(userId))
    } catch (e) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [userId, authUserId, t])

  useEffect(() => {
    void fetchHomeworks()
  }, [fetchHomeworks])

  const approve = async (homework: Homework) => {
    setBusy(true)
    try {
      await homeworksApi.checkByUser(userId, homework.id, { action: 'approve' })
      await fetchHomeworks()
    } catch (e) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setBusy(false)
    }
  }

  const sendBack = async () => {
    if (!revision) return
    setBusy(true)
    try {
      await homeworksApi.checkByUser(userId, revision.homework.id, {
        action: 'revision',
        comment: revision.comment,
      })
      setRevision(null)
      await fetchHomeworks()
    } catch (e) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setBusy(false)
    }
  }

  const statusLabel = (hw: Homework) => {
    if (hw.checked_at) return t('homeworks.statusChecked')
    if (hw.teacher_feedback) return t('homeworks.statusRevision', 'На доработке')
    if (hw.completed_at) return t('homeworks.statusCompleted')

    return t('homeworks.statusAssigned')
  }

  const visible = homeworks.slice(page * perPage, page * perPage + perPage)

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <PageBreadcrumbs
          items={[
            { label: t('dashboard.students'), to: ROUTES.USERS },
            {
              label: studentName || t('homeworks.studentFallback', { id: userId }),
              to: isAdminRole ? ROUTES.ADMIN_USER_VIEW(userId) : `/users/${userId}`,
            },
            { label: t('homeworks.title') },
          ]}
        />

        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flex: 1 }}>
            {t('homeworks.title')}
          </Typography>
          <PageHelpPopover pageKey="homeworks" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
        ) : homeworks.length === 0 ? (
          <Card>
            <CardContent>
              <Typography color="text.secondary">{t('homeworks.empty', 'Нет заданий')}</Typography>
            </CardContent>
          </Card>
        ) : (
          <TableContainer component={Card}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 48 }} />
                  <TableCell>{t('homeworks.tableCreatedAt', 'Дата создания')}</TableCell>
                  <TableCell>{t('homeworks.tableTask', 'Задание')}</TableCell>
                  <TableCell>{t('homeworks.tableStatus', 'Статус')}</TableCell>
                  <TableCell>{t('exercises.check.score')}</TableCell>
                  <TableCell align="right">{t('common.actions', 'Действия')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((hw) => (
                  <Fragment key={hw.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => setExpandedId(expandedId === hw.id ? null : hw.id)}
                          aria-label={t('common.details', 'Подробнее')}
                        >
                          {expandedId === hw.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          {formatCreatedAt(hw.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={ROUTES.LESSON_DETAILS(hw.lesson_id)}
                          underline="hover"
                          sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                        >
                          {t('homeworks.fromLesson', 'Урок #{{id}}', { id: hw.lesson_id })}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={hw.checked_at ? 'success' : hw.completed_at ? 'warning' : 'default'}
                          label={statusLabel(hw)}
                        />
                      </TableCell>
                      <TableCell>
                        {typeof hw.score_percent === 'number' ? `${hw.score_percent}%` : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(event) => setMenu({ anchor: event.currentTarget, homework: hw })}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0, borderBottom: 'none' }}>
                        <Collapse in={expandedId === hw.id} unmountOnExit>
                          <Box sx={{ py: 2 }}>
                            <HomeworkItemsView homework={hw} withAnswers />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={homeworks.length}
              page={page}
              onPageChange={(_, value) => setPage(value)}
              rowsPerPage={perPage}
              rowsPerPageOptions={[10, 25, 50]}
              onRowsPerPageChange={(event) => {
                setPerPage(parseInt(event.target.value, 10))
                setPage(0)
              }}
              labelRowsPerPage={t('common.rowsPerPage', 'Строк на странице')}
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common.of', 'из')} ${count}`}
            />
          </TableContainer>
        )}

        <Menu anchorEl={menu?.anchor ?? null} open={menu !== null} onClose={() => setMenu(null)}>
          <MenuItem
            disabled={busy || Boolean(menu?.homework.checked_at)}
            onClick={() => {
              const target = menu?.homework
              setMenu(null)
              if (target) void approve(target)
            }}
          >
            {t('homeworks.checkAction', 'Проверить')}
          </MenuItem>
          {/* Вернуть можно и проверенную: домашка закрывается сама, как только
            проверен последний пункт, и запрет отнимал бы у учителя право
            передумать ровно в тот момент, когда он дочитал работу */}
          <MenuItem
            disabled={busy}
            onClick={() => {
              const target = menu?.homework
              setMenu(null)
              if (target) setRevision({ homework: target, comment: '' })
            }}
          >
            {t('homeworks.revisionAction', 'Вернуть на доработку')}
          </MenuItem>
        </Menu>

        <Dialog open={revision !== null} onClose={() => setRevision(null)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('homeworks.revisionAction', 'Вернуть на доработку')}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('homeworks.revisionHint', 'Комментарий увидит ученик — по нему он и будет исправлять')}
              </Typography>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={3}
                label={t('exercises.check.comment')}
                value={revision?.comment ?? ''}
                onChange={(event) =>
                  setRevision((current) => (current ? { ...current, comment: event.target.value } : current))
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRevision(null)}>{t('common.cancel')}</Button>
            <Button
              variant="contained"
              disabled={busy || (revision?.comment.trim() ?? '') === ''}
              onClick={() => void sendBack()}
            >
              {t('common.send', 'Отправить')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
